import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  createStatusSnippetService,
  deleteStatusSnippetService,
  getStatusTemplatesService,
  resetStatusDefaultService,
  setStatusDefaultService,
  updateStatusSnippetService,
} from "@/services/settings.service";
import { IAPIResponse } from "@/services/api.types";
import type {
  StatusDefault,
  StatusSnippet,
  StatusSnippetDeleted,
  StatusTemplatesPayload,
} from "@/types/settings.types";

export const statusTemplateQueryKeys = {
  all: (orgId: string) => ["status-templates", orgId] as const,
};

export function useStatusTemplates(orgId: string) {
  return useQuery<StatusTemplatesPayload, Error>({
    queryKey: statusTemplateQueryKeys.all(orgId),
    queryFn: () => getStatusTemplatesService(orgId),
    enabled: Boolean(orgId),
  });
}

function useRefresh(orgId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: statusTemplateQueryKeys.all(orgId) });
}

export function useSetStatusDefault(orgId: string) {
  const refresh = useRefresh(orgId);
  return useMutation<IAPIResponse<StatusDefault>, Error, { slot: string; body: string }>({
    mutationFn: ({ slot, body }) => setStatusDefaultService(orgId, slot, body),
    onSuccess: (response) => { toast.success(response.message); refresh(); },
    onError: (error) => toast.error(error.message),
  });
}

export function useResetStatusDefault(orgId: string) {
  const refresh = useRefresh(orgId);
  return useMutation<IAPIResponse<StatusDefault>, Error, { slot: string }>({
    mutationFn: ({ slot }) => resetStatusDefaultService(orgId, slot),
    onSuccess: (response) => { toast.success(response.message); refresh(); },
    onError: (error) => toast.error(error.message),
  });
}

export function useCreateSnippet(orgId: string) {
  const refresh = useRefresh(orgId);
  return useMutation<IAPIResponse<StatusSnippet>, Error, { title: string; body: string }>({
    mutationFn: (payload) => createStatusSnippetService(orgId, payload),
    onSuccess: (response) => { toast.success(response.message); refresh(); },
    onError: (error) => toast.error(error.message),
  });
}

export function useUpdateSnippet(orgId: string) {
  const refresh = useRefresh(orgId);
  return useMutation<IAPIResponse<StatusSnippet>, Error, { id: string; title?: string; body?: string }>({
    mutationFn: ({ id, ...patch }) => updateStatusSnippetService(orgId, id, patch),
    onSuccess: (response) => { toast.success(response.message); refresh(); },
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteSnippet(orgId: string) {
  const refresh = useRefresh(orgId);
  return useMutation<IAPIResponse<StatusSnippetDeleted>, Error, { id: string }>({
    mutationFn: ({ id }) => deleteStatusSnippetService(orgId, id),
    onSuccess: (response) => { toast.success(response.message); refresh(); },
    onError: (error) => toast.error(error.message),
  });
}
